/**
 * InvoiceListScreen — desktop-native dense table of recent invoices.
 *
 *  ┌───────────────────────────────────────────────────────────┐
 *  │ Search · Type filter · Status filter · Date range          │
 *  ├───────────────────────────────────────────────────────────┤
 *  │  #  │ Date    │ Customer │ Type │ Total  │ Paid │ Status   │
 *  ├───────────────────────────────────────────────────────────┤
 *  │  …  │  …      │  …       │  …   │  …     │  …   │  …       │
 *  └───────────────────────────────────────────────────────────┘
 *
 * All queries are reactive via WatermelonDB observe().
 * Click a row → navigates to /invoices/:id.
 */

import { useEffect, useMemo, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, FileText, CalendarDays } from 'lucide-react';
import { database } from '@/db';
import { Invoice, Payment } from '@/db/models/Billing';
import { Customer } from '@/db/models/Customers';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { Money } from '@/components/ui/numeric';

type RangeKey = 'TODAY' | '7D' | '30D' | 'MTD' | 'ALL';

/**
 * Start of the selected window as epoch ms, or null for all time.
 *
 * Day boundaries are local, not UTC: a shop's "today" ends when the shutter
 * comes down, and in IST a UTC day boundary falls at 5:30am — which would put
 * the early-morning sales of one day into the previous one.
 */
function rangeStart(key: RangeKey): number | null {
  if (key === 'ALL') return null;
  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  switch (key) {
    case 'TODAY': return midnight;
    case '7D': return midnight - 6 * 86_400_000;
    case '30D': return midnight - 29 * 86_400_000;
    case 'MTD': return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  }
}

type TypeFilter = 'ALL' | 'SALE' | 'CREDIT_NOTE';
type StatusFilter = 'ALL' | 'PAID' | 'PARTIAL' | 'UNPAID';

const TYPE_LABEL: Record<string, string> = {
  SALE: 'Sale',
  CREDIT_NOTE: 'Credit note',
  PURCHASE: 'Purchase',
  ESTIMATE: 'Estimate',
};

export function InvoiceListScreen() {
  const navigate = useNavigate();
  const activeStoreId = useAppStore((s) => s.activeStoreId);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim().toLowerCase()), 180);
    return () => clearTimeout(t);
  }, [searchInput]);

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('ALL');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [range, setRange] = useState<RangeKey>('30D');

  // Resolved to a timestamp so it can go INTO the query. Filtering 500 fetched
  // rows in memory would quietly cap the range at the most recent 500 invoices
  // — "this month" would start lying as soon as a store passed that in a month.
  const sinceMs = useMemo(() => rangeStart(range), [range]);

  // ── Invoices (reactive) ──────────────────────────────────
  const invoices = useObservable<Invoice[]>(
    () => {
      if (!activeStoreId) return null;
      const conds: any[] = [
        Q.where('store_id', activeStoreId),
        Q.where('is_deleted', false),
      ];
      if (sinceMs !== null) conds.push(Q.where('created_at', Q.gte(sinceMs)));
      if (typeFilter !== 'ALL') conds.push(Q.where('type', typeFilter));
      if (statusFilter !== 'ALL') conds.push(Q.where('payment_status', statusFilter));
      if (searchQuery.length > 0) {
        // Invoice-number search only — customer/phone search joins live below.
        conds.push(Q.where('invoice_number', Q.like(`%${searchQuery}%`)));
      }
      conds.push(Q.sortBy('created_at', Q.desc));
      conds.push(Q.take(500));
      return database
        .get<Invoice>('invoices')
        .query(...conds)
        .observeWithColumns(['payment_status', 'total_amount', 'paid_amount']);
    },
    [activeStoreId, searchQuery, typeFilter, statusFilter, sinceMs],
    [],
  );

  // ── Customer name lookup ─────────────────────────────────
  // Build a single map from the unique customerIds present in the result
  // set instead of N parallel finds — keeps the UI snappy on big lists.
  const customerIds = useMemo(
    () => Array.from(new Set(invoices.map((i) => i.customerId).filter((x): x is string => !!x))),
    [invoices],
  );
  const [customerNames, setCustomerNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (customerIds.length === 0) {
      setCustomerNames({});
      return;
    }
    let cancelled = false;
    (async () => {
      const map: Record<string, string> = {};
      // SQLite IN-list cap of ~999 — batch in 500s.
      for (let i = 0; i < customerIds.length; i += 500) {
        const batch = customerIds.slice(i, i + 500);
        const rows = await database
          .get<Customer>('customers')
          .query(Q.where('id', Q.oneOf(batch)))
          .fetch();
        for (const c of rows) map[c.id] = c.name?.trim() || c.phone || 'Unnamed';
      }
      if (!cancelled) setCustomerNames(map);
    })();
    return () => { cancelled = true; };
  }, [customerIds]);

  // ── Payment-method lookup ────────────────────────────────
  // Batched the same way as the names above, for the same reason. A bill can
  // be split across methods (part cash, part UPI), so this keeps the full set
  // per invoice rather than the first one — "Cash +1" is the honest summary,
  // and a single method is by far the common case.
  const invoiceIds = useMemo(() => invoices.map((i) => i.id), [invoices]);
  const [payMethods, setPayMethods] = useState<Record<string, string[]>>({});
  useEffect(() => {
    if (invoiceIds.length === 0) {
      setPayMethods({});
      return;
    }
    let cancelled = false;
    (async () => {
      const map: Record<string, string[]> = {};
      for (let i = 0; i < invoiceIds.length; i += 500) {
        const batch = invoiceIds.slice(i, i + 500);
        const rows = await database
          .get<Payment>('payments')
          .query(Q.where('invoice_id', Q.oneOf(batch)), Q.where('is_deleted', false))
          .fetch();
        for (const r of rows) {
          const list = map[r.invoiceId] ?? (map[r.invoiceId] = []);
          if (!list.includes(r.method)) list.push(r.method);
        }
      }
      if (!cancelled) setPayMethods(map);
    })();
    return () => { cancelled = true; };
  }, [invoiceIds]);

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Header / filters */}
      {/* Wraps, and the search keeps a floor — four controls plus a search
          field do not fit on one line on a laptop. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[15rem] flex-1">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-tertiary" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search invoice number…"
            className="h-9 pl-9"
          />
        </div>
        <FilterChip<RangeKey>
          icon={<CalendarDays size={12} />}
          options={[
            { value: 'TODAY', label: 'Today' },
            { value: '7D', label: 'Last 7 days' },
            { value: '30D', label: 'Last 30 days' },
            { value: 'MTD', label: 'This month' },
            { value: 'ALL', label: 'All time' },
          ]}
          value={range}
          onChange={setRange}
        />
        <FilterChip<TypeFilter>
          icon={<FileText size={12} />}
          options={[
            { value: 'ALL', label: 'All types' },
            { value: 'SALE', label: 'Sales' },
            { value: 'CREDIT_NOTE', label: 'Returns' },
          ]}
          value={typeFilter}
          onChange={setTypeFilter}
        />
        <FilterChip<StatusFilter>
          icon={<Filter size={12} />}
          options={[
            { value: 'ALL', label: 'All statuses' },
            { value: 'PAID', label: 'Paid' },
            { value: 'PARTIAL', label: 'Partial' },
            { value: 'UNPAID', label: 'Unpaid' },
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </div>

      {/* Table */}
      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
        <table className="w-full text-body">
          <thead className="sticky top-0 bg-bg-surface text-label uppercase text-fg-tertiary">
            <tr className="border-b border-stroke-hairline/10">
              <th className="px-4 py-2 text-left font-normal">Invoice</th>
              <th className="px-4 py-2 text-left font-normal">Date</th>
              <th className="px-4 py-2 text-left font-normal">Customer</th>
              <th className="px-4 py-2 text-left font-normal">Payment</th>
              <th className="px-4 py-2 text-left font-normal">Type</th>
              <th className="px-4 py-2 text-right font-normal">Total</th>
              <th className="px-4 py-2 text-right font-normal">Paid</th>
              <th className="px-4 py-2 text-right font-normal">Due</th>
              <th className="px-4 py-2 text-left font-normal">Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-fg-tertiary">
                  No invoices match these filters.
                </td>
              </tr>
            ) : (
              invoices.map((inv) => (
                <tr
                  key={inv.id}
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                  className="cursor-pointer border-b border-stroke-hairline/10 transition-colors hover:bg-bg-subtle"
                >
                  <td className="px-4 py-2.5 font-medium text-fg-primary">{inv.invoiceNumber}</td>
                  <td className="px-4 py-2.5 text-fg-secondary tabular-nums">
                    {new Date(inv.createdAt).toLocaleDateString(undefined, {
                      day: '2-digit', month: 'short', year: '2-digit',
                    })}
                    <span className="ml-1.5 text-fg-tertiary">
                      {new Date(inv.createdAt).toLocaleTimeString(undefined, {
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-fg-secondary">
                    {inv.customerId ? (customerNames[inv.customerId] ?? '…') : <span className="text-fg-tertiary">Walk-in</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <PaymentCell methods={payMethods[inv.id]} due={inv.dueAmount} />
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={cn(
                      'inline-flex items-center rounded px-1.5 py-0.5 text-label uppercase',
                      inv.type === 'CREDIT_NOTE'
                        ? 'bg-warning/10 text-warning-ink'
                        : 'bg-bg-subtle text-fg-secondary',
                    )}>
                      {TYPE_LABEL[inv.type] ?? inv.type}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-fg-primary tabular-nums">
                    <Money value={inv.totalAmount} />
                  </td>
                  <td className="px-4 py-2.5 text-right text-fg-secondary tabular-nums">
                    <Money value={inv.paidAmount} />
                  </td>
                  <td className={cn(
                    'px-4 py-2.5 text-right tabular-nums',
                    inv.dueAmount > 0 ? 'text-warning-ink' : 'text-fg-tertiary',
                  )}>
                    <Money value={inv.dueAmount} />
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={inv.paymentStatus} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
//  Pieces
// ═════════════════════════════════════════════════════════════════════

function FilterChip<T extends string>({
  icon,
  options,
  value,
  onChange,
}: {
  icon: React.ReactNode;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  const current = options.find((o) => o.value === value)?.label ?? '';
  return (
    <label className="relative inline-flex items-center gap-2 rounded-md border border-stroke-hairline/15 bg-bg-surface px-3 h-9 text-body text-fg-secondary cursor-pointer hover:border-accent/40 hover:text-fg-primary transition-colors">
      <span className="text-fg-tertiary">{icon}</span>
      <span>{current}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="absolute inset-0 cursor-pointer opacity-0"
        aria-label="Filter"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'PAID' ? 'bg-success/10 text-success-ink' :
    status === 'PARTIAL' ? 'bg-warning/10 text-warning-ink' :
    status === 'UNPAID' ? 'bg-danger/10 text-danger-ink' :
    'bg-bg-subtle text-fg-secondary';
  return (
    <span className={cn('inline-flex items-center rounded px-1.5 py-0.5 text-label uppercase', tone)}>
      {status}
    </span>
  );
}

/**
 * How the bill was settled.
 *
 * Deliberately not a rainbow of one colour per method. Cash, UPI and card are
 * categories, not states — colouring them would spend the palette on something
 * that needs no decision and leave nothing to mark the row that does. The one
 * case worth a colour is money still owed, so an unpaid bill reads red and
 * everything else stays neutral.
 *
 * A split payment shows the first method plus a count rather than a list, so
 * the column keeps one line per row; the detail is on the invoice itself.
 */
function PaymentCell({ methods, due }: { methods?: string[]; due: number }) {
  if (due > 0 && (!methods || methods.length === 0)) {
    return (
      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-label uppercase bg-danger/10 text-danger-ink">
        Unpaid
      </span>
    );
  }
  if (!methods || methods.length === 0) {
    return <span className="text-label text-fg-tertiary">—</span>;
  }
  const [first, ...rest] = methods;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-flex items-center rounded bg-bg-subtle px-1.5 py-0.5 text-label uppercase text-fg-secondary">
        {PAYMENT_LABEL[first] ?? first}
      </span>
      {rest.length > 0 && (
        <span className="text-label text-fg-tertiary">+{rest.length}</span>
      )}
    </span>
  );
}

const PAYMENT_LABEL: Record<string, string> = {
  CASH: 'Cash',
  UPI: 'UPI',
  CARD: 'Card',
  CREDIT: 'Khata',
  BANK_TRANSFER: 'Bank',
  CHEQUE: 'Cheque',
  WALLET: 'Wallet',
};
